import { useMemo } from 'react';
import { getValueFromFormData } from '../../Shared/Helpers/getValueFromFormData';
import { isDiscountAmountDisabled, reservationFields } from '../fields';
import { FormField } from '../../Shared/Components/FormRenderer';
import { OfferBoardingPlace } from '../../Shared/Types/offerBoardingPlacesTypes';
import { ReservationPartyLookup } from '../Service/reservationPartiesLookupMapper';
import { getReservationPartyDraftFields } from '../Helpers/getReservationPartyDraftFields';
import type { ApplicantLookupItem } from '../Service/applicantsLookupMapper';
import { OfferOptionType } from '../../OfferOptionsMappings/Types/offerOptionsMappingsTypes';
import { ReservationBookingLookupOption } from '../Types/reservationBookingContext.types';

type ApplicantForField = Pick<ApplicantLookupItem, 'id' | 'firstName' | 'lastName' | 'email'> & {
  displayName?: string;
  phone?: string | null;
  city?: string | null;
  zipCode?: string | null;
  type?: string | null;
  addressSummary?: string | null;
};

const EMPTY_APPLICANTS: ApplicantForField[] = [];

interface Props {
  formData: any;
  bookingLookupOptions: ReservationBookingLookupOption[];
  bookingLookupLoading: boolean;
  applicants?: ApplicantForField[];
  applicantsLoading?: boolean;
  reservationParties: ReservationPartyLookup[];
  reservationPartiesLoading: boolean;
  offerBoardingPlaces: OfferBoardingPlace[];
  offerBoardingPlacesLoading: boolean;
  offerOptionTypes: OfferOptionType[];
  offerOptionTypesLoading: boolean;
  hasOfferOptions: boolean;
  hideBookingField?: boolean;
}

/**
 * Hook for generating dynamic reservation fields.
 * Handles only fields from reservationFields!
 */
export function useReservationFields({
  formData,
  bookingLookupOptions,
  bookingLookupLoading,
  applicants = EMPTY_APPLICANTS,
  applicantsLoading = false,
  reservationParties,
  reservationPartiesLoading,
  offerBoardingPlaces,
  offerBoardingPlacesLoading,
  offerOptionTypes,
  offerOptionTypesLoading,
  hasOfferOptions,
  hideBookingField = false,
}: Props): FormField<any>[] {
  return useMemo(() => {
    const hasActiveTotalPriceOverride = isDiscountAmountDisabled(formData?.totalPriceOverride);
    const mappedFields = reservationFields.map((field: FormField<any>) => {
      if (field.name === 'bookingId') {
        return {
          ...field,
          hidden: hideBookingField,
          value: getValueFromFormData(formData, String(field.name)),
          options: bookingLookupOptions,
          loading: bookingLookupLoading,
        };
      }

      if (field.name === 'applicantId' && applicants) {
        return {
          ...field,
          value: getValueFromFormData(formData, String(field.name)),
          options: applicants.map((a: ApplicantForField) => ({
            label: a.displayName ?? `${a.firstName} ${a.lastName} (${a.email})`,
            value: a.id,
            description: a.addressSummary || a.email || null,
            applicantData: {
              firstName: a.firstName,
              lastName: a.lastName,
              email: a.email,
              phone: a.phone,
            },
          })),
          loading: applicantsLoading,
        };
      }

      if (
        (field.name === 'applicantPartyId' ||
          field.name === 'invoiceBuyerPartyId' ||
          field.name === 'fundingPartyId') &&
        reservationParties
      ) {
        return {
          ...field,
          value: getValueFromFormData(formData, String(field.name)),
          options: reservationParties.map((party: ReservationPartyLookup) => ({
            label: party.label,
            value: party.id,
            description: party.addressSummary || party.email || party.phone,
            partyData: party,
          })),
          loading: reservationPartiesLoading,
        };
      }

      if (field.name === 'offerBoardingPlaceId' && offerBoardingPlaces) {
        return {
          ...field,
          value: getValueFromFormData(formData, String(field.name)),
          options: (offerBoardingPlaces ?? []).map((bp: OfferBoardingPlace) => ({
            label: bp.city + (bp.price ? ` (+${bp.price} zł)` : ''),
            value: bp.id,
          })),
          loading: offerBoardingPlacesLoading,
        };
      }

      if (field.name === 'discountAmount') {
        return {
          ...field,
          value: getValueFromFormData(formData, String(field.name)),
          disabled: hasActiveTotalPriceOverride,
          helperText: hasActiveTotalPriceOverride
            ? 'Rabat jest ignorowany przy aktywnej nadpisanej cenie'
            : field.helperText,
        };
      }

      if (field.name === 'selectedOfferOptions') {
        return {
          ...field,
          value: getValueFromFormData(formData, String(field.name)) ?? [],
          optionTypes: offerOptionTypes,
          loading: offerOptionTypesLoading,
          hidden: !hasOfferOptions,
        };
      }
      // --- Przykład na przyszłość (opcje obligatoryjne) ---
      // if (field.name === 'mandatoryOptionIds' && mandatoryOptions) {
      //   return {
      //     ...field,
      //     type: 'checkboxGroup',
      //     options: (mandatoryOptions ?? []).map((opt: MandatoryOption) => ({
      //       label: `${opt.name} (+${opt.price} zł)`,
      //       value: opt.id,
      //     })),
      //     loading: mandatoryOptionsLoading,
      //   };
      // }

      // Hide additionalPayment in CreateReservation (only for updates)
      if (field.name === 'additionalPayment' && !formData.id) {
        return { ...field, hidden: true };
      }

      // Standardowa obsługa value
      return {
        ...field,
        value: getValueFromFormData(formData, String(field.name)),
      };
    });

    const additionalFields: FormField<any>[] = [];

    if (formData.applicantPartyId === '__new__') {
      additionalFields.push(
        ...getReservationPartyDraftFields(
          'applicantPartyDraft',
          'Nowa strona zgłaszająca',
          formData.applicantPartyDraft
        )
      );
    }

    if (formData.invoiceBuyerPartyId === '__new__') {
      additionalFields.push(
        ...getReservationPartyDraftFields(
          'invoiceBuyerPartyDraft',
          'Nowa strona faktury',
          formData.invoiceBuyerPartyDraft
        )
      );
    }

    if (formData.fundingPartyId === '__new__') {
      additionalFields.push(
        ...getReservationPartyDraftFields(
          'fundingPartyDraft',
          'Nowa strona finansująca',
          formData.fundingPartyDraft
        )
      );
    }

    return [...mappedFields, ...additionalFields];
  }, [
    formData,
    bookingLookupOptions,
    bookingLookupLoading,
    applicants,
    applicantsLoading,
    reservationParties,
    reservationPartiesLoading,
    offerBoardingPlaces,
    offerBoardingPlacesLoading,
    offerOptionTypes,
    offerOptionTypesLoading,
    hasOfferOptions,
    hideBookingField,
    // mandatoryOptions,
    // mandatoryOptionsLoading,
  ]);
}
